import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../../../components/layouts/authenticated_layout'
import RadioButtonGroup from '../../../components/buttons/radio_button_group'
import { useEnterpriseDetail } from '../../../hooks/use_enterprise_detail'
import KuchikomiEditTab from './tabs/kuchikomi_edit_tab'
import OverviewEditTab from './tabs/overview_edit_tab'
import SdgsEditTab from './tabs/sdgs_edit_tab'
import CoCreationEditTab from './tabs/co_creation_edit_tab'
import type { RadioButtonType } from '../../../components/buttons/radio_button_group'
import FlatButton from '../../../components/buttons/flat_button'
import LoadingSpinner from '../../../components/loading/loading_spinner'
import { useUpdateEnterprise } from '../hooks/use_update_enterprise'
import { useAuthAuthorized } from '../../../hooks/auth/use_auth'
import { useSnackBar } from '../../../providers/snack_bar_provider'
import { EditFormData, EditTab } from './types'
import { useForm, FieldErrors } from 'react-hook-form'

const EnterprisesDetailEditPage: React.FC = () => {
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState<EditTab>('kuchikomi')
  const { isUpdating, updateEnterprise } = useUpdateEnterprise()

  const { user } = useAuthAuthorized()
  const { showSnackBar } = useSnackBar()
  const { enterprise, enterpriseSummary, loading, error } = useEnterpriseDetail(
    user.kuchikomiEnterpriseId
  )

  const {
    register,
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditFormData>({
    values: {
      kuchikomi: {
        name: enterpriseSummary?.name || '',
        description: enterpriseSummary?.description || '',
        delegateLastName: enterpriseSummary?.delegateLastName || '',
        delegateFirstName: enterpriseSummary?.delegateFirstName || '',
        delegatePosition: enterpriseSummary?.delegatePosition || '',
        responsiblePersonName: enterpriseSummary?.responsiblePersonName || '',
        establishDate: enterpriseSummary?.establishDate || '',
        prefecture: enterpriseSummary?.prefecture || '',
        postCode: enterpriseSummary?.postCode || '',
        address: enterpriseSummary?.address || '',
        tel: enterpriseSummary?.tel || '',
        stockCode: enterpriseSummary?.stockCode || '',
        capitalPrice: enterpriseSummary?.capitalPrice || null,
        employeeNum: enterpriseSummary?.employeeNum || null,
        logoUrl: enterpriseSummary?.logoUrl || null,
        officialUrl: enterpriseSummary?.officialUrl || '',
        twitterUrl: enterpriseSummary?.twitterUrl || '',
        facebookUrl: enterpriseSummary?.facebookUrl || '',
        lineUrl: enterpriseSummary?.lineUrl || '',
        tiktokUrl: enterpriseSummary?.tiktokUrl || '',
        industryParentCode: enterpriseSummary?.industryParentCode?.code || '',
        industryChildCode: enterpriseSummary?.industryChildCode?.code || '',
        enterpriseCode: enterpriseSummary?.enterpriseCode?.code || '',
        businessCode1: enterpriseSummary?.businessCode?.[0]?.code || '',
        businessChildCode1:
          enterpriseSummary?.businessCode?.[0]?.child?.code || '',
        businessCode2: enterpriseSummary?.businessCode?.[1]?.code || '',
        businessChildCode2:
          enterpriseSummary?.businessCode?.[1]?.child?.code || '',
        businessCode3: enterpriseSummary?.businessCode?.[2]?.code || '',
        businessChildCode3:
          enterpriseSummary?.businessCode?.[2]?.child?.code || '',
        interestingSDGs:
          enterpriseSummary?.sdgsCategory?.map((s) => s.code) || [],
      },
      overview: {
        bannerImageUrl: enterprise?.summary?.bannerImageUrl || '',
        topImages:
          enterprise?.summary?.topImages?.images?.map((image) => ({
            id: image.id,
            imageUrl: image.imageUrl,
            sortOrder: image.sortOrder,
          })) || [],
        companyProfile: enterprise?.summary?.companyProfile || '',
        companyFeature: enterprise?.summary?.companyFeature || '',
        providedResource: enterprise?.summary?.providedResource || '',
        majorCustomer: enterprise?.summary?.majorCustomer || '',
        shareholder: enterprise?.summary?.shareholder || '',
        openInnovationAchievements:
          enterprise?.summary?.openInnovationAchievements?.achievements?.map(
            (achievement) => ({
              id: achievement.id,
              imageUrl: achievement.imageUrl || '',
              content: achievement.content || '',
            })
          ) || [],
        departmentName:
          enterprise?.publicationResponsibility?.departmentName || '',
        responsibleLastName:
          enterprise?.publicationResponsibility?.responsiblePerson?.lastName ||
          '',
        responsibleFirstName:
          enterprise?.publicationResponsibility?.responsiblePerson?.firstName ||
          '',
        responsiblePosition:
          enterprise?.publicationResponsibility?.responsiblePerson?.position ||
          '',
        responsibleProfileImg:
          enterprise?.publicationResponsibility?.responsiblePerson
            ?.profileImg || '',
        responsibleIntroduction:
          enterprise?.publicationResponsibility?.responsiblePerson
            ?.introduction || '',
        responsibleMission:
          enterprise?.publicationResponsibility?.responsiblePerson?.mission ||
          '',
        responsibleComment:
          enterprise?.publicationResponsibility?.responsiblePerson?.comment ||
          '',
        departmentDescription:
          enterprise?.publicationResponsibility?.departmentDescription || '',
        searchIndustryCategories:
          enterprise?.searchIndustryCategories?.map((category) => ({
            id: category.id,
            industryParentCode: category.industryParentCode,
            industryChildCode: category.industryChildCode,
          })) || [],
        companyTypes: enterprise?.companyTypes || [],
        companyStage: enterprise?.companyStage || null,
        servicePurposes: enterprise?.servicePurposes || [],
      },
      sdgs: {
        renewableEnergy: enterprise?.sdgsInitiatives?.renewableEnergy || '',
        energyEfficiency: enterprise?.sdgsInitiatives?.energyEfficiency || '',
        environmentalEducation:
          enterprise?.sdgsInitiatives?.environmentalEducation || '',
        sdgsInfo: enterprise?.sdgsInitiatives?.sdgsInfo || '',
      },
      coCreation: {
        title: enterprise?.coCreationInfo?.title || '',
        content: enterprise?.coCreationInfo?.content || '',
        issue: enterprise?.coCreationInfo?.issue || '',
        goal: enterprise?.coCreationInfo?.goal || '',
        requirement: enterprise?.coCreationInfo?.requirement || '',
      },
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (enterprise !== null && !enterprise.isOwn) {
      showSnackBar(['ぺージが見つかりません'], 'error')
      navigate(`/enterprises/detail/${user.kuchikomiEnterpriseId}`)
    }
  }, [
    enterprise,
    enterpriseSummary,
    user.kuchikomiEnterpriseId,
    navigate,
    showSnackBar,
  ])

  const onSubmitError = (errors: FieldErrors<EditFormData>) => {
    // エラーがあるタブを検出
    const firstErrorTab =
      (errors.kuchikomi &&
        Object.keys(errors.kuchikomi).length > 0 &&
        'kuchikomi') ||
      (errors.overview &&
        Object.keys(errors.overview).length > 0 &&
        'overview') ||
      (errors.sdgs && Object.keys(errors.sdgs).length > 0 && 'sdgs') ||
      (errors.coCreation &&
        Object.keys(errors.coCreation).length > 0 &&
        'co-creation') ||
      null

    if (firstErrorTab) {
      // エラーがあるタブに切り替え
      setSelectedTab(firstErrorTab)

      // エラー要素へのスクロール（タブ切り替え後に実行）
      setTimeout(() => {
        const errorElement = document.querySelector('.has-error')
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }, 100) // タブ切り替えのアニメーション待ち
    }
  }

  // 企業詳細更新
  const handleUpdateEnterprise = async (data: EditFormData) => {
    const result = await updateEnterprise(user.kuchikomiEnterpriseId, data)

    if (result) {
      navigate(`/enterprises/detail/${user.kuchikomiEnterpriseId}`)
    }
  }

  // 編集キャンセル
  const handleCancel = () => {
    navigate(`/enterprises/detail/${user.kuchikomiEnterpriseId}`)
  }

  // タブボタンの定義（編集モードでは全て表示）
  const tabButtons: RadioButtonType[] = useMemo(
    () => [
      {
        label: '基本情報',
        onClick: () => setSelectedTab('kuchikomi'),
      },
      {
        label: '企業概要',
        onClick: () => setSelectedTab('overview'),
      },
      {
        label: 'SDGsの取り組み',
        onClick: () => setSelectedTab('sdgs'),
      },
      {
        label: '共創希望',
        onClick: () => setSelectedTab('co-creation'),
      },
    ],
    []
  )

  // アクティブなタブのインデックス
  const activeTabIndex = useMemo(() => {
    switch (selectedTab) {
      case 'kuchikomi':
        return 0
      case 'overview':
        return 1
      case 'sdgs':
        return 2
      case 'co-creation':
        return 3
      default:
        return 0
    }
  }, [selectedTab])

  const isLoading = loading
  if (isLoading) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  if (error || !enterprise) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-main-darkgray mb-4">
              企業が見つかりません
            </h1>
            <p className="text-main-darkgray">
              {error || '指定された企業情報を取得できませんでした。'}
            </p>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <div className="container mx-auto px-4 py-8 bg-main-white">
        {/* 編集モードは単一カラム */}
        <div className="grid grid-cols-1 gap-8">
          <div className="col-span-1">
            <div className="flex justify-between items-start mb-6">
              {/* タブナビゲーション */}
              <div className="flex-1">
                <div className="inline-flex">
                  <RadioButtonGroup
                    buttons={tabButtons}
                    activeIndex={activeTabIndex}
                  />
                </div>
              </div>

              {/* 編集ボタン */}
              <div className="flex gap-2">
                <FlatButton onClick={handleCancel} outline>
                  編集をやめる
                </FlatButton>
                <FlatButton
                  onClick={handleSubmit(handleUpdateEnterprise, onSubmitError)}
                  disabled={isUpdating}
                >
                  {isUpdating ? '更新中...' : '更新する'}
                </FlatButton>
              </div>
            </div>

            {/* タブコンテンツ */}
            <div className="border border-main-lightgray rounded px-6 pb-6 pt-0">
              <div className={selectedTab === 'kuchikomi' ? 'block' : 'hidden'}>
                <KuchikomiEditTab
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  control={control}
                  errors={errors}
                />
              </div>

              <div className={selectedTab === 'overview' ? 'block' : 'hidden'}>
                <OverviewEditTab
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  control={control}
                  errors={errors}
                />
              </div>

              <div className={selectedTab === 'sdgs' ? 'block' : 'hidden'}>
                <SdgsEditTab
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                />
              </div>

              <div
                className={selectedTab === 'co-creation' ? 'block' : 'hidden'}
              >
                <CoCreationEditTab
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  control={control}
                  errors={errors}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 更新中のローディングオーバーレイ */}
        {isUpdating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center py-20">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  )
}

export default EnterprisesDetailEditPage
